import {
  Box,
  FormControlLabel,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import InputOutlinedIcon from '@mui/icons-material/InputOutlined';
import { Client } from '../../../data/models/client.model';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';
import { ClientPurposeCode } from '../../../data/models/purpose-code-model.model';

const PREFIX = 'PurposeCodeList';

const classes = {
  container: `${PREFIX}-container`,
  purposeCode: `${PREFIX}-purposeCode`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    gap: theme.spacing(1),
  },

  [`& .${classes.purposeCode}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#bce1f5',
    borderRadius: 4,
    padding: theme.spacing(0.875),
  },
}));

interface PurposeCodeListProps {
  client: Client;
  purposeCodes: ClientPurposeCode[];
  onActivate: (purposeCode: ClientPurposeCode) => void;
  onDeactivate: (purposeCode: ClientPurposeCode) => void;
  onDelete: (purposeCode: ClientPurposeCode) => void;
  onStartDrag: (purposeCode: ClientPurposeCode) => void;
  onEndDrag: (purposeCode: ClientPurposeCode) => void;
}

export default function PurposeCodeList({
  client,
  purposeCodes,
  onActivate,
  onDeactivate,
  onDelete,
  onStartDrag,
  onEndDrag,
}: PurposeCodeListProps) {
  const t = useCommonTranslation();

  const isSuperReaderUser = useIsSuperReaderUser();

  const handleActiveChange = (evt: any, purposeCode: ClientPurposeCode) => {
    if (evt.target.checked) {
      onActivate(purposeCode);
    } else {
      onDeactivate(purposeCode);
    }
  };

  return (
    <StyledBox className={classes.container}>
      {purposeCodes.map((purposeCode) => (
        <Box
          key={purposeCode.purposeCodeUUID}
          className={classes.purposeCode}
          draggable={purposeCode.state === 'active'}
          onDragStart={() => onStartDrag(purposeCode)}
          onDragEnd={() => onEndDrag(purposeCode)}>
          <Box className={classes.purposeCode}>{purposeCode.name}</Box>
          <Box>
            {!isSuperReaderUser && (
              <Tooltip title={t('client.deletePurposeCode')}>
                <span>
                  <IconButton
                    data-testid="delete-purpose-code"
                    onClick={() => onDelete(purposeCode)}
                    color="error"
                    size="large"
                    disabled={purposeCode.state === 'active'}>
                    <DeleteForeverIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {!isSuperReaderUser && (
              <Tooltip
                title={
                  purposeCode.state === 'active'
                    ? t('client.active')
                    : t('client.inactive')
                }>
                <Switch
                  checked={purposeCode.state === 'active'}
                  onChange={(evt) => handleActiveChange(evt, purposeCode)}
                />
              </Tooltip>
            )}
          </Box>
        </Box>
      ))}
    </StyledBox>
  );
}
